// src/components/Header/Header.js

import React, { useState, useEffect, useRef } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { animateScroll as scroll } from "react-scroll";
import DIETLogo from "../PIC/Logo/DIET_Logo.png";
import ACMLogo from "../PIC/Logo/DIET_ACM_Logo.png";
import "./Header.css";

function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const headerRef = useRef(null);

  // Detect active page
  const isBlogsPageActive = location.pathname.startsWith('/blogs');
  const isEventsPageActive = location.pathname.startsWith('/events');
  const isOurTeamPageActive = location.pathname.startsWith('/ourteam');
  const isGalleryPageActive = location.pathname.startsWith('/gallery');
  const isBenefitsPageActive = location.pathname.startsWith('/benefits');
  const isCalendarPageActive = location.pathname.startsWith('/calendar');

  const [activeSection, setActiveSection] = useState(
    (isBlogsPageActive || isEventsPageActive || isOurTeamPageActive ||
     isGalleryPageActive || isBenefitsPageActive || isCalendarPageActive)
      ? null : "home"
  );

  // === STICKY HEADER LOGIC ===
  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;

    const stickyPoint = header.offsetTop; // Where header starts

    const handleScroll = () => {
      const scrolledPast = window.pageYOffset > stickyPoint;
      setIsSticky(scrolledPast);
      setScrolled(window.scrollY > 30); // For background blur effect
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initial check

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // === DYNAMIC HEADER HEIGHT (for First.js padding) ===
  useEffect(() => {
    const updateHeaderHeight = () => {
      const header = headerRef.current;
      if (header) {
        const height = header.offsetHeight;
        document.documentElement.style.setProperty("--header-height", `${height}px`);
      }
    };

    updateHeaderHeight();
    window.addEventListener("resize", updateHeaderHeight);

    // Re-calculate when mobile menu opens/closes
    const menuBtn = document.querySelector(".menu-button");
    if (menuBtn) {
      menuBtn.addEventListener("click", updateHeaderHeight);
    }

    return () => {
      window.removeEventListener("resize", updateHeaderHeight);
      if (menuBtn) menuBtn.removeEventListener("click", updateHeaderHeight);
    };
  }, [isMenuOpen]);

  const onNavClick = (section) => {
    setActiveSection(section);
    setIsMenuOpen(false);
  };

  const toggleMenu = () => {
    setIsMenuOpen(prev => !prev);
  };

  const navItems = ["home", "blogs", "events", "calendar", "ourteam", "gallery", "benefits"];

  return (
    <header
      ref={headerRef}
      className={`main-header ${scrolled ? "scrolled" : ""} ${isSticky ? "sticky" : ""}`}
    >
      <div className="logo-container">
        <a href="https://diet.edu.in/" target="_blank" rel="noopener noreferrer">
          <img src={DIETLogo} alt="DIET Logo" className="diet-logo" />
        </a>
        <RouterLink
          to="/"
          onClick={() => {
            onNavClick("home");
            scroll.scrollToTop();
          }}
        >
          <img src={ACMLogo} alt="ACM Logo" className="acm-logo" />
        </RouterLink>
      </div>

      {/* HAMBURGER MENU BUTTON */}
      <button
        className="menu-button"
        onClick={toggleMenu}
        aria-expanded={isMenuOpen}
        aria-label="Toggle navigation menu"
      >
        &#9776;
      </button>

      <nav className={`nav-links ${isMenuOpen ? "open" : ""}`}>
        {navItems.map((section) => {
          const isActiveOnDedicatedPage =
            (section === 'blogs' && isBlogsPageActive) ||
            (section === 'events' && isEventsPageActive) ||
            (section === 'calendar' && isCalendarPageActive) ||
            (section === 'ourteam' && isOurTeamPageActive) ||
            (section === 'gallery' && isGalleryPageActive) ||
            (section === 'benefits' && isBenefitsPageActive);

          // HOME LINK
          if (section === "home") {
            const isRootActive = location.pathname === '/' && !isActiveOnDedicatedPage;
            return (
              <RouterLink
                key={section}
                to="/"
                className={isRootActive ? "active" : ""}
                onClick={() => onNavClick(section)}
              >
                {section.toUpperCase()}
              </RouterLink>
            );
          }

          // OTHER PAGES
          if (['blogs', 'events', 'calendar', 'ourteam', 'gallery', 'benefits'].includes(section)) {
            return (
              <RouterLink
                key={section}
                to={`/${section}`}
                className={isActiveOnDedicatedPage ? "active" : ""}
                onClick={() => onNavClick(section)}
              >
                {section.toUpperCase()}
              </RouterLink>
            );
          }

          return null;
        })}
      </nav>
    </header>
  );
}

export default Header;